import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { DEPRECATED_backwardsCompatibleValues, useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { formatMoney } from "@faire/web--source/common/money";
import { Checkbox } from "@faire/web--source/slate/Checkbox";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography, Span } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackProductInfoProductDetailsCustomizeItemToggleClick } from "@faire/web-api--source/events/productInfo/click/productDetailsCustomizeItemToggle";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { SERRANO } from "@faire/retailer-visitor-shared/consts/colors";
import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { useGetSettingAndAssignUpdateSaleColorCopy } from "@faire/retailer/settings/useSettingUpdateSaleColorCopy";
// max length allowed by DB
var INPUT_CHARACTERS_HARD_LIMIT = 1024;
var CustomizationBase = function(param) {
    var value = param.value, onChange = param.onChange, onToggle = param.onToggle, instructions = param.instructions, characterLimit = param.characterLimit, additionalCharge = param.additionalCharge, hideAdditionalCharge = param.hideAdditionalCharge, additionalPromoCharge = param.additionalPromoCharge, required = param.required, wholesalePrice = param.wholesalePrice;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useState = _sliced_to_array(useState(value !== ""), 2), isChecked = _useState[0], setIsChecked = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), touchedTextBox = _useState1[0], setTouchedTextBox = _useState1[1];
    var hasToggledInitially = useRef(false);
    useEffect(function() {
        if (!hasToggledInitially.current) {
            onToggle(isChecked);
            hasToggledInitially.current = true;
        }
    }, [
        isChecked,
        onToggle
    ]);
    var onCheckboxChange = function() {
        if (!isChecked) {
            trackProductInfoProductDetailsCustomizeItemToggleClick();
        }
        setIsChecked(function(prev) {
            onToggle(!prev);
            return !prev;
        });
        onChange("");
    };
    var handleTextAreaChange = function(e) {
        if (!touchedTextBox) {
            setTouchedTextBox(true);
        }
        onChange(e.target.value);
    };
    var showRequired = value.trim().length === 0 && touchedTextBox;
    var labelText = required ? strictLocalize({
        id: "3r9k3k",
        defaultMessage: [
            {
                type: 1,
                value: "instructionsFullyTranslatedSentence"
            },
            {
                type: 0,
                value: "*"
            }
        ]
    }, {
        instructionsFullyTranslatedSentence: instructions
    }) : instructions;
    var errorMessage = showRequired ? strictLocalize({
        id: "RqO/6d",
        defaultMessage: [
            {
                type: 0,
                value: "This field is required"
            }
        ]
    }) : undefined;
    // Passing a maxCharacters of 0 to TextAreaInput implies
    // unbounded length. This prevents that from causing errors
    // in the BE.
    var maxCharacters = characterLimit === 0 ? INPUT_CHARACTERS_HARD_LIMIT : characterLimit;
    if (required) {
        return /*#__PURE__*/ _jsxs(Container, {
            children: [
                wholesalePrice ? /*#__PURE__*/ _jsx(Typography, {
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "enF4zm",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "This customized item is "
                            },
                            {
                                type: 1,
                                value: "unitPriceMoney"
                            },
                            {
                                type: 0,
                                value: "/unit"
                            }
                        ],
                        values: {
                            unitPriceMoney: wholesalePrice
                        }
                    })
                }) : null,
                /*#__PURE__*/ _jsx(Row, {
                    children: /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.subdued,
                        variant: "labelSansRegular",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "gRntvb",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Customized items are not returnable"
                                }
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 10
                }),
                /*#__PURE__*/ _jsx(TextArea, {
                    label: labelText,
                    value: value,
                    onChange: handleTextAreaChange,
                    maxLength: maxCharacters,
                    required: showRequired,
                    error: errorMessage,
                    style: {
                        height: "125px"
                    },
                    "data-test-id": "addToCartCustomizationRequiredTextArea"
                })
            ]
        });
    } else {
        var _additionalPromoCharge_discounted_item_price;
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsxs(Container, {
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsxs(CheckboxContainer, {
                                children: [
                                    /*#__PURE__*/ _jsx(Checkbox, {
                                        checked: isChecked,
                                        onChange: onCheckboxChange,
                                        "data-test-id": "addToCartCustomizationCheckbox"
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "1x"
                                    }),
                                    /*#__PURE__*/ _jsx(CheckboxLabel, {
                                        onClick: onCheckboxChange,
                                        children: hideAdditionalCharge ? null : /*#__PURE__*/ _jsx(Typography, {
                                            "data-test-id": "customization-additional-charge",
                                            children: showProductPromotions(additionalPromoCharge) ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "TvnbMR",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "Customize this item for "
                                                    },
                                                    {
                                                        type: 8,
                                                        value: "Strikethrough",
                                                        children: [
                                                            {
                                                                type: 1,
                                                                value: "additionalCharge"
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        type: 0,
                                                        value: " "
                                                    },
                                                    {
                                                        type: 8,
                                                        value: "Gold",
                                                        children: [
                                                            {
                                                                type: 1,
                                                                value: "additionalPromoCharge"
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        type: 0,
                                                        value: "/unit"
                                                    }
                                                ],
                                                values: DEPRECATED_backwardsCompatibleValues({
                                                    Strikethrough: StrikeThroughFormatting,
                                                    Gold: GoldFormatting,
                                                    additionalCharge: formatMoney({
                                                        money: additionalCharge
                                                    }),
                                                    additionalPromoCharge: formatMoney({
                                                        money: additionalPromoCharge.discounted_item_price
                                                    })
                                                })
                                            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "o+zsa1",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "Customize this item for "
                                                    },
                                                    {
                                                        type: 1,
                                                        value: "additionalCharge"
                                                    },
                                                    {
                                                        type: 0,
                                                        value: "/unit"
                                                    }
                                                ],
                                                values: DEPRECATED_backwardsCompatibleValues({
                                                    additionalCharge: formatMoney({
                                                        money: (_additionalPromoCharge_discounted_item_price = additionalPromoCharge === null || additionalPromoCharge === void 0 ? void 0 : additionalPromoCharge.discounted_item_price) !== null && _additionalPromoCharge_discounted_item_price !== void 0 ? _additionalPromoCharge_discounted_item_price : additionalCharge
                                                    })
                                                })
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Row, {
                                children: [
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: "3x"
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        color: Core.text.subdued,
                                        variant: "labelSansRegular",
                                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                            id: "gRntvb",
                                            defaultMessage: [
                                                {
                                                    type: 0,
                                                    value: "Customized items are not returnable"
                                                }
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    isChecked ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 10
                            }),
                            /*#__PURE__*/ _jsx(TextArea, {
                                label: labelText,
                                value: value,
                                onChange: handleTextAreaChange,
                                maxLength: maxCharacters,
                                required: showRequired,
                                error: errorMessage,
                                style: {
                                    height: "125px"
                                },
                                "data-test-id": "addToCartCustomizationOptionalTextArea"
                            })
                        ]
                    }) : null
                ]
            })
        });
    }
};
export var Customization = observer(CustomizationBase);
var StrikeThroughFormatting = function(chunk) {
    return /*#__PURE__*/ _jsx(StrikeThrough, {
        children: chunk
    });
};
var GoldFormatting = function(chunk) {
    var shouldUpdateAndAssignSaleColor = useGetSettingAndAssignUpdateSaleColorCopy();
    return /*#__PURE__*/ _jsx(Span, {
        color: shouldUpdateAndAssignSaleColor ? SERRANO : Decorative.shade.orange,
        children: chunk
    });
};
var Container = styled.div.withConfig({
    componentId: "sc-be6bb998-0"
})([
    "width:100%;"
]);
var CheckboxContainer = styled(Row).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-be6bb998-1"
})([
    "flex-wrap:nowrap;"
]);
var CheckboxLabel = styled.label.withConfig({
    componentId: "sc-be6bb998-2"
})([
    "display:flex;justify-content:space-between;flex:1 0 auto;cursor:pointer;"
]);
var StrikeThrough = styled.span.withConfig({
    componentId: "sc-be6bb998-3"
})([
    "text-decoration:line-through;color:",
    ";"
], Core.text.subdued);
